'use client';

/**
 * useSlashCommandMenu Hook
 *
 * Manages all state and logic for the "/" slash command search menu.
 * Encapsulates slash detection, search term tracking, and result selection.
 */

import { useState, useCallback, RefObject } from 'react';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { UnifiedSearchResult } from './useUnifiedCriteriaSearch';
import {
  getTextNodeAtCursor,
  findSlashPosition,
  getPositionRect,
  removeTextRange,
  findWordEndOffset,
  moveCursorToEnd,
} from '../utils/editorDomUtils';
import { mergeItemIntoInput } from '../utils/criteriaTransformer';

interface Position {
  x: number;
  y: number;
}

interface ComponentData {
  id: string;
  type: CriteriaComponentType;
  [key: string]: any;
}

interface UseSlashCommandMenuParams {
  editorRef: RefObject<HTMLDivElement>;
  components: Map<string, ComponentData>;
  dynamicCriteria: any;
  portals: Map<string, any>;
  effectiveAllowedTypes: CriteriaComponentType[];
  updateComponentInput: (componentId: string, data: any) => void;
  updateCriteria: () => void;
  insertComponentPill: (type: CriteriaComponentType, initialData?: Record<string, unknown>) => string | undefined;
  setPendingPortalUpdate: (update: { componentId: string; updatedData: any; target: HTMLElement | undefined } | null) => void;
}

interface UseSlashCommandMenuReturn {
  showSlashMenu: boolean;
  slashMenuPosition: Position;
  slashSearchTerm: string;
  slashStartNode: Node | null;
  slashStartOffset: number | null;
  detectSlashCommand: () => void;
  handleSlashSelect: (result: UnifiedSearchResult) => void;
  handleSlashMenuClose: () => void;
  resetSlashState: () => void;
}

export function useSlashCommandMenu({
  editorRef,
  components,
  dynamicCriteria,
  portals,
  effectiveAllowedTypes,
  updateComponentInput,
  updateCriteria,
  insertComponentPill,
  setPendingPortalUpdate,
}: UseSlashCommandMenuParams): UseSlashCommandMenuReturn {
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState<Position>({ x: 0, y: 0 });
  const [slashStartOffset, setSlashStartOffset] = useState<number | null>(null);
  const [slashStartNode, setSlashStartNode] = useState<Node | null>(null);
  const [slashSearchTerm, setSlashSearchTerm] = useState('');

  /**
   * Find an existing component of the same type in the editor.
   * Used to determine if we should merge or create a new component.
   */
  const findExistingComponentOfType = useCallback(
    (type: CriteriaComponentType): { id: string; data: ComponentData } | null => {
      const entries = Array.from(components.entries());
      for (let i = 0; i < entries.length; i++) {
        const [id, component] = entries[i];
        if (component.type === type) {
          return { id, data: component };
        }
      }
      return null;
    },
    [components]
  );

  /**
   * Add an item to an existing component (for slash menu merge).
   * This avoids creating duplicate components of the same type.
   * Uses centralized transformer for data merging.
   */
  const addItemToExistingComponent = useCallback(
    (componentId: string, _type: CriteriaComponentType, newItem: Record<string, unknown>) => {
      const existing = dynamicCriteria?.criteria_inputs?.[componentId];
      if (!existing) return;

      // Use centralized transformer for merging
      const updatedData = mergeItemIntoInput(existing, newItem);

      // If data didn't change (duplicate or static/singleton), skip update
      if (updatedData === existing) {
        return;
      }

      updateComponentInput(componentId, updatedData);

      // Store the data needed for portal re-render
      setPendingPortalUpdate({
        componentId,
        updatedData,
        target: portals.get(componentId)?.target,
      });

      // Trigger criteria update
      setTimeout(() => {
        updateCriteria();
      }, 0);
    },
    [dynamicCriteria?.criteria_inputs, updateComponentInput, updateCriteria, portals, setPendingPortalUpdate]
  );

  /**
   * Resets all slash command tracking state.
   */
  const resetSlashState = useCallback(() => {
    setShowSlashMenu(false);
    setSlashStartOffset(null);
    setSlashStartNode(null);
    setSlashSearchTerm('');
  }, []);

  /**
   * Detects slash command trigger from current cursor position.
   * Called from the editor's onInput handler.
   */
  const detectSlashCommand = useCallback(() => {
    const textNodeInfo = getTextNodeAtCursor();
    if (!textNodeInfo) {
      resetSlashState();
      return;
    }

    const { node: textNode, offset: cursorPos } = textNodeInfo;
    const text = textNode.textContent || '';

    const slashPos = findSlashPosition(text, cursorPos);

    if (slashPos !== -1) {
      // Extract the search term (text after the slash, before cursor)
      const searchText = text.substring(slashPos + 1, cursorPos);
      setSlashSearchTerm(searchText);

      // Calculate position for menu - position below the slash
      const rect = getPositionRect(textNode, slashPos);
      if (rect) {
        setSlashMenuPosition({
          x: rect.left,
          y: rect.bottom + 5,
        });
      }

      setSlashStartOffset(slashPos);
      setSlashStartNode(textNode);
      setShowSlashMenu(true);
    } else {
      // No slash found, close menu if open
      resetSlashState();
    }
  }, [resetSlashState]);

  /**
   * Handles selection of a search result from the slash menu.
   * Either merges into an existing component or creates a new one.
   */
  const handleSlashSelect = useCallback(
    (result: UnifiedSearchResult) => {
      if (!editorRef.current) return;

      // Remove the "/searchterm" text from the editor
      if (slashStartNode && slashStartOffset !== null) {
        try {
          const textContent = slashStartNode.textContent || '';
          const endOffset = findWordEndOffset(textContent, slashStartOffset + 1);

          const success = removeTextRange(slashStartNode, slashStartOffset, endOffset);
          if (!success) {
            moveCursorToEnd(editorRef.current);
          }
        } catch (e) {
          moveCursorToEnd(editorRef.current);
        }
      }

      // Check if we should merge into an existing component of the same type
      if (effectiveAllowedTypes.includes(result.criteriaType)) {
        const existingComponent = findExistingComponentOfType(result.criteriaType);

        if (existingComponent) {
          // Merge into existing component
          addItemToExistingComponent(
            existingComponent.id,
            result.criteriaType,
            result.originalData
          );
        } else {
          // Create new component
          insertComponentPill(result.criteriaType, result.originalData);
        }
      }

      // Reset slash tracking
      resetSlashState();
      editorRef.current.focus();
    },
    [
      editorRef,
      slashStartNode,
      slashStartOffset,
      effectiveAllowedTypes,
      insertComponentPill,
      findExistingComponentOfType,
      addItemToExistingComponent,
      resetSlashState,
    ]
  );

  /**
   * Handles closing the slash menu.
   * Removes the "/" and any search text from the editor.
   */
  const handleSlashMenuClose = useCallback(() => {
    if (slashStartNode && slashStartOffset !== null) {
      try {
        const textContent = slashStartNode.textContent || '';
        const endOffset = findWordEndOffset(textContent, slashStartOffset + 1);
        removeTextRange(slashStartNode, slashStartOffset, endOffset);
      } catch (e) {
        // Ignore DOM manipulation errors
      }
    }

    resetSlashState();
    editorRef.current?.focus();
  }, [slashStartNode, slashStartOffset, resetSlashState, editorRef]);

  return {
    showSlashMenu,
    slashMenuPosition,
    slashSearchTerm,
    slashStartNode,
    slashStartOffset,
    detectSlashCommand,
    handleSlashSelect,
    handleSlashMenuClose,
    resetSlashState,
  };
}
